import ConfirmModal from 'components/confirm-modal/confirm-modal';
import { ListHolidayItem } from 'generated/api-client';
import {
  useDeleteHoliday,
  useListHolidays,
} from 'generated/api-client/@tanstack/react-query.gen';
import { HolidayFormModal } from 'modules/holiday/components/HolidayFormModal';
import { HolidayStatus } from 'modules/holiday/components/HolidayStatus';
import { holidayStatus } from 'modules/holiday/utils/holidayStatus';
import React, { Fragment, FunctionComponent, useState } from 'react';
import {
  Button,
  Header,
  Icon,
  Loader,
  Message,
  Table,
} from 'semantic-ui-react';
import { ToastError, ToastSuccess } from 'utils';
import { formatCalendarDate, todayIn } from 'utils/date';

type Props = {
  viewId: number;
};

export const HolidayList: FunctionComponent<Props> = ({ viewId }) => {
  const [edited, setEdited] = useState<{
    open: boolean;
    holiday?: ListHolidayItem;
  }>({ open: false });

  const { data, isLoading, isError, refetch } = useListHolidays({
    query: { viewIds: [viewId] },
  });

  const { mutateAsync: deleteHoliday } = useDeleteHoliday();

  const holidays = data?.holidays ?? [];
  const today = todayIn('Europe/Paris');

  const remove = async (holidayId: number): Promise<void> => {
    try {
      await deleteHoliday({ path: { holidayId } });
      ToastSuccess('Congé', 'Congé supprimé');
      await refetch();
    } catch {
      ToastError('Erreur', 'Impossible de supprimer le congé');
    }
  };

  if (isError) {
    return (
      <Message negative>
        Impossible de charger les congés. Rechargez la page pour réessayer.
      </Message>
    );
  }

  if (isLoading) {
    return (
      <Loader
        style={{ marginTop: '50px' }}
        size="large"
        active
        inline="centered"
      >
        Chargement...
      </Loader>
    );
  }

  return (
    <Fragment>
      <Header>
        Congés
        <Button
          floated="right"
          positive
          type="button"
          onClick={(): void => setEdited({ open: true })}
        >
          <Icon name="plus" />
          Nouveau congé
        </Button>
      </Header>

      {holidays.length > 0 ? (
        <Table striped celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Date de début</Table.HeaderCell>
              <Table.HeaderCell>Date de fin</Table.HeaderCell>
              <Table.HeaderCell>Statut</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {holidays.map((holiday) => {
              const status = holidayStatus(
                holiday.startDate,
                holiday.endDate,
                today,
              );
              const isEnded = status === 'ended';

              return (
                <Table.Row key={holiday.id}>
                  <Table.Cell>
                    {formatCalendarDate(holiday.startDate)}
                  </Table.Cell>
                  <Table.Cell>{formatCalendarDate(holiday.endDate)}</Table.Cell>
                  <Table.Cell>
                    <HolidayStatus status={status} />
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Button
                      icon
                      primary
                      type="button"
                      disabled={isEnded}
                      style={{ minWidth: '35px', minHeight: '32px' }}
                      onClick={(): void => setEdited({ open: true, holiday })}
                    >
                      <Icon name="pencil alternate" />
                    </Button>
                    <ConfirmModal
                      header="Supprimer ce congé ?"
                      content={
                        <p>
                          Le congé du {formatCalendarDate(holiday.startDate)} au{' '}
                          {formatCalendarDate(holiday.endDate)} sera
                          définitivement supprimé.
                        </p>
                      }
                      onConfirm={(): Promise<void> => remove(holiday.id)}
                      trigger={
                        <Button
                          icon
                          negative
                          basic
                          type="button"
                          disabled={isEnded}
                          style={{ minWidth: '35px', minHeight: '32px' }}
                        >
                          <Icon name="trash alternate outline" />
                        </Button>
                      }
                    />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      ) : (
        <Message>Aucun congé</Message>
      )}

      <HolidayFormModal
        open={edited.open}
        viewId={viewId}
        holiday={edited.holiday}
        viewHolidays={holidays}
        onClose={(): void => setEdited({ open: false })}
        onSaved={(): void => {
          refetch();
        }}
      />
    </Fragment>
  );
};
